import { type FC, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import type { MasterEntityType } from '@api/entity-attributes';
import PageHeader from '@components/common/PageHeader';
import EntityMasterTab from '@components/containers/EntityMaster/EntityMasterTab';
import type { FilterValue } from '@components/ui/DataSheet';
import SegmentedToggle from '@components/ui/SegmentedToggle';

type CatalogView = 'products' | 'materials';

const VIEW_ENTITY: Record<CatalogView, MasterEntityType> = {
    products: 'PRODUCT',
    materials: 'MATERIAL',
};

const VIEW_OPTIONS = [
    { value: 'products', label: 'Products' },
    { value: 'materials', label: 'Materials' },
];

/** `?f.category=Puff|Chips` — one key per filter, values joined by `|`. */
const FILTER_PREFIX = 'f.';
const FILTER_JOIN = '|';

const readFilters = (params: URLSearchParams): FilterValue => {
    const value: FilterValue = {};
    for (const [key, raw] of params.entries()) {
        if (!key.startsWith(FILTER_PREFIX) || !raw) continue;
        value[key.slice(FILTER_PREFIX.length)] = raw.split(FILTER_JOIN).filter(Boolean);
    }
    return value;
};

/**
 * The catalogue: what the brand sells and what goes into it, one tab each.
 *
 * The tab, the search and the filters live in the URL, so Back from a product's page lands
 * on the same list, and a link to "/catalog?view=materials&f.materialType=RM" is a link to
 * that list. Written with `replace` while typing so a search does not leave one history
 * entry per keystroke.
 */
const CatalogContainer: FC = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const view: CatalogView = params.get('view') === 'materials' ? 'materials' : 'products';
    const entityType = VIEW_ENTITY[view];
    const initialSearch = params.get('q') ?? '';
    const initialFilterValue = useMemo(() => readFilters(params), [params]);

    const switchView = (next: string) => {
        // A new tab starts clean: its filters are its own.
        const nextParams = new URLSearchParams();
        nextParams.set('view', next);
        setParams(nextParams);
    };

    const writeSearch = useCallback(
        (search: string) => {
            setParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    if (search) next.set('q', search);
                    else next.delete('q');
                    return next;
                },
                { replace: true },
            );
        },
        [setParams],
    );

    const writeFilters = useCallback(
        (filterValue: FilterValue) => {
            setParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    for (const key of [...next.keys()]) if (key.startsWith(FILTER_PREFIX)) next.delete(key);
                    for (const [key, values] of Object.entries(filterValue)) {
                        if (values && values.length > 0) next.set(`${FILTER_PREFIX}${key}`, values.join(FILTER_JOIN));
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setParams],
    );

    const openRecord = useCallback(
        (id: string) => {
            void navigate(`/catalog/${view}/${encodeURIComponent(id)}`);
        },
        [navigate, view],
    );

    return (
        <div className="flex h-full w-full flex-col gap-4 page-shell">
            <PageHeader
                title="Catalog"
                subtitle="Everything the brand sells and everything that goes into it. Open a row for its makers, prices and recipe."
                actions={<SegmentedToggle value={view} onChange={switchView} options={VIEW_OPTIONS} />}
            />
            <EntityMasterTab
                key={view}
                entityType={entityType}
                onOpenRecord={openRecord}
                maxHeight={undefined}
                initialSearch={initialSearch}
                initialFilterValue={initialFilterValue}
                onSearchChange={writeSearch}
                onFilterValueChange={writeFilters}
            />
        </div>
    );
};

export default CatalogContainer;
